import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const { 
  FiBarChart2, FiEye, FiUsers, FiClock, FiTrendingUp, FiGlobe, 
  FiCalendar, FiDownload, FiHeart, FiPieChart, FiMap
} = FiIcons;

const AnalyticsPage = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  const overviewStats = [
    {
      name: 'Total Views',
      value: '24.5K',
      change: '+12.5%',
      changeType: 'increase',
      icon: FiEye,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Unique Visitors',
      value: '8.2K',
      change: '+8.1%',
      changeType: 'increase',
      icon: FiUsers,
      color: 'from-purple-500 to-pink-500'
    },
    {
      name: 'Average Time',
      value: '3m 42s',
      change: '-0.8%',
      changeType: 'decrease',
      icon: FiClock,
      color: 'from-green-500 to-emerald-500'
    },
    {
      name: 'Conversion Rate',
      value: '3.2%',
      change: '+0.5%',
      changeType: 'increase',
      icon: FiTrendingUp,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const topGalleries = [
    {
      id: 1,
      name: 'Summer Wedding Collection',
      views: 1250,
      likes: 87,
      downloads: 34,
      thumbnail: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=100&h=80&fit=crop'
    },
    {
      id: 2,
      name: 'Urban Portrait Series',
      views: 980,
      likes: 65,
      downloads: 21,
      thumbnail: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&h=80&fit=crop'
    },
    {
      id: 3,
      name: 'Natural Landscapes',
      views: 745,
      likes: 52,
      downloads: 18,
      thumbnail: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=100&h=80&fit=crop'
    }
  ];

  const topCountries = [
    { name: 'United States', value: 42 },
    { name: 'United Kingdom', value: 18 },
    { name: 'Canada', value: 8 },
    { name: 'Australia', value: 7 },
    { name: 'Germany', value: 6 }
  ];

  const deviceData = [
    { name: 'Desktop', value: 52 },
    { name: 'Mobile', value: 38 },
    { name: 'Tablet', value: 10 }
  ];

  const referrerData = [
    { name: 'Direct', value: 40 },
    { name: 'Instagram', value: 25 },
    { name: 'Google', value: 15 },
    { name: 'Facebook', value: 12 },
    { name: 'Pinterest', value: 8 }
  ];

  const visitorData = [
    { date: '2023-01-01', visitors: 120 },
    { date: '2023-01-02', visitors: 132 },
    { date: '2023-01-03', visitors: 101 },
    { date: '2023-01-04', visitors: 134 },
    { date: '2023-01-05', visitors: 90 },
    { date: '2023-01-06', visitors: 110 },
    { date: '2023-01-07', visitors: 120 },
    { date: '2023-01-08', visitors: 132 },
    { date: '2023-01-09', visitors: 101 },
    { date: '2023-01-10', visitors: 134 },
    { date: '2023-01-11', visitors: 90 },
    { date: '2023-01-12', visitors: 110 },
    { date: '2023-01-13', visitors: 120 },
    { date: '2023-01-14', visitors: 132 }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'galleries', label: 'Galleries', icon: FiCamera },
    { id: 'audience', label: 'Audience', icon: FiUsers },
    { id: 'revenue', label: 'Revenue', icon: FiDollarSign }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {overviewStats.map((stat, index) => (
                <motion.div
                  key={stat.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {stat.name}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {stat.value}
                      </p>
                      <p className={`text-sm mt-1 ${
                        stat.changeType === 'increase' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {stat.change} from last period
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                      <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Visitor Chart */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Visitor Traffic
                </h3>
                <Button variant="outline" size="sm" icon={FiDownload}>
                  Export
                </Button>
              </div>
              
              <div className="h-80">
                <div className="h-full flex flex-col">
                  <div className="flex-1 flex items-end">
                    {visitorData.map((item, index) => (
                      <div 
                        key={item.date} 
                        className="flex-1 flex flex-col items-center group"
                      >
                        <div 
                          className="w-full max-w-[40px] bg-primary-500 hover:bg-primary-600 rounded-t-lg relative group transition-colors"
                          style={{ height: `${(item.visitors / 140) * 100}%` }}
                        >
                          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {item.visitors} visitors
                          </div>
                        </div>
                        {(index % 2 === 0 || index === visitorData.length - 1) && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                            {new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="h-6"></div>
                </div>
              </div>
            </div>

            {/* Top Galleries and Countries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Galleries */}
              <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Top Performing Galleries
                </h3>
                <div className="space-y-4">
                  {topGalleries.map((gallery) => (
                    <div key={gallery.id} className="flex items-center space-x-4">
                      <div className="w-20 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={gallery.thumbnail}
                          alt={gallery.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {gallery.name}
                        </h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <SafeIcon icon={FiEye} className="w-4 h-4 mr-1" />
                            {gallery.views}
                          </span>
                          <span className="flex items-center">
                            <SafeIcon icon={FiHeart} className="w-4 h-4 mr-1" />
                            {gallery.likes}
                          </span>
                          <span className="flex items-center">
                            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                            {gallery.downloads}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Countries */}
              <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Audience by Country
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiMap} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="space-y-3">
                  {topCountries.map((country, index) => (
                    <div key={country.name} className="flex items-center">
                      <span className="text-sm text-gray-700 dark:text-gray-300 w-32">
                        {country.name}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full"
                          style={{ width: `${country.value}%` }}
                        />
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {country.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Device and Referrer Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Device Distribution */}
              <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Device Distribution
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiPieChart} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="flex space-x-6 justify-center">
                  {deviceData.map((device, index) => (
                    <div key={device.name} className="flex flex-col items-center">
                      <div 
                        className="w-24 h-24 rounded-full border-8 flex items-center justify-center"
                        style={{ 
                          borderColor: index === 0 ? '#3B82F6' : index === 1 ? '#8B5CF6' : '#10B981'
                        }}
                      >
                        <span className="text-lg font-bold" style={{ 
                          color: index === 0 ? '#3B82F6' : index === 1 ? '#8B5CF6' : '#10B981'
                        }}>
                          {device.value}%
                        </span>
                      </div>
                      <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                        {device.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Traffic Sources */}
              <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Traffic Sources
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={FiGlobe} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="space-y-3">
                  {referrerData.map((source) => (
                    <div key={source.name} className="flex items-center">
                      <span className="text-sm text-gray-700 dark:text-gray-300 w-24">
                        {source.name}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-secondary-500 rounded-full"
                          style={{ width: `${source.value}%` }}
                        />
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {source.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'galleries':
      case 'audience':
      case 'revenue':
        return (
          <div className="text-center p-12 text-gray-600 dark:text-gray-400">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={tabs.find(tab => tab.id === activeTab)?.icon} className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              {tabs.find(tab => tab.id === activeTab)?.label} Analytics
            </h2>
            <p className="mb-6 max-w-md mx-auto">
              Detailed {activeTab} analytics will be available here. This feature is coming soon with more in-depth data visualization.
            </p>
            <Button variant="outline">
              Coming Soon
            </Button>
          </div>
        );
      
      default:
        return (
          <div className="text-center p-12 text-gray-600 dark:text-gray-400">
            Select a tab to view content
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Analytics
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track performance metrics for your galleries and website
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white dark:bg-dark-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="12m">Last 12 months</option>
              </select>
            </div>
            <Button variant="outline" icon={FiDownload}>
              Export Report
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center py-4 px-6 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-gray-50 dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {renderTabContent()}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Adding FiCamera and FiDollarSign for the analytics tabs
const FiCamera = FiIcons.FiCamera;
const FiDollarSign = FiIcons.FiDollarSign;

export default AnalyticsPage;